import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthAPI, setTokens, getAccessToken, getRefreshToken, clearTokens } from '../../api/client';
import { requestCache } from '../../utils/requestCache';

// Initialize auth state from localStorage
const initializeAuth = () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  return {
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
    accessToken,
    refreshToken,
  };
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      // Use JWT login
      const { access, refresh } = await AuthAPI.loginJWT(username, password);
      setTokens(access, refresh);
      const user = await AuthAPI.me();
      return { user, access, refresh };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Login failed');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      // Don't fetch if we already have user data and it's recent
      if (state.auth.user && state.auth.isAuthenticated) {
        return state.auth.user;
      }

      const user = await AuthAPI.me();
      return user;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch user');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async () => {
    clearTokens();
    // Clear auth-related cache when logging out
    requestCache.clear();
    return null;
  }
);

export const initializeAuthState = createAsyncThunk(
  'auth/initializeAuthState',
  async (_, { dispatch, getState }) => {
    const state = getState();

    // If already initialized and authenticated, don't re-initialize
    if (state.auth.user && state.auth.isAuthenticated) {
      return state.auth.user;
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (accessToken && refreshToken) {
      // Don't need to dispatch setTokens here, tokens are already in localStorage
      // Just fetch the current user
      try {
        const result = await dispatch(fetchCurrentUser());
        return result.payload;
      } catch (error) {
        // If fetch fails, clear tokens
        clearTokens();
        throw error;
      }
    }
    return null;
  }
);

const initialState = initializeAuth();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setTokens: (state, action) => {
      state.accessToken = action.payload.access;
      state.refreshToken = action.payload.refresh;
      state.isAuthenticated = !!(action.payload.access && action.payload.refresh);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Fetch current user
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = null;
      })
      // Initialize auth state
      .addCase(initializeAuthState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAuthState.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeAuthState.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.refreshToken = null;
      });
  },
});

export const { clearError, setTokens: setTokensAction } = authSlice.actions;
export default authSlice.reducer;
